import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { referralAPI, campaignAPI } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { Link2, Plus, Copy, ExternalLink, Trash2, BarChart3, Eye, EyeOff, Check, X, ChevronDown, ChevronUp, Globe, Smartphone, Monitor, Tablet, MousePointerClick } from 'lucide-react';
import Pagination from '../components/Pagination';
import { SkeletonTable } from '../components/LoadingSkeleton';
import './ReferralLinks.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';
const BASE_URL = API_URL.replace('/api/v1', '');

function ReferralLinks() {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const [expandedLink, setExpandedLink] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});

  // Create form state
  const [createForm, setCreateForm] = useState({
    name: '',
    custom_code: '',
    destination_url: '',
    campaign_id: '',
  });

  // Fetch referral links
  const { data, isLoading } = useQuery({
    queryKey: ['referral-links', page],
    queryFn: () => referralAPI.getAll({ page, limit: 20 }).then(r => r.data),
    keepPreviousData: true,
  });

  // Fetch campaigns for dropdown
  const { data: campaignData } = useQuery({
    queryKey: ['campaigns-list'],
    queryFn: () => campaignAPI.getAll().then(r => r.data),
  });

  const campaigns = campaignData?.campaigns || campaignData || [];

  // Fetch stats for an expanded link
  const { data: linkDetails } = useQuery({
    queryKey: ['referral-link-detail', expandedLink],
    queryFn: () => referralAPI.getById(expandedLink).then(r => r.data),
    enabled: !!expandedLink,
  });

  // Create mutation
  const createMut = useMutation({
    mutationFn: (data) => referralAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['referral-links'] });
      setShowCreate(false);
      setCreateForm({ name: '', custom_code: '', destination_url: '', campaign_id: '' });
    },
  });

  // Update mutation
  const updateMut = useMutation({
    mutationFn: ({ id, data }) => referralAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['referral-links'] });
      queryClient.invalidateQueries({ queryKey: ['referral-link-detail'] });
      setEditingId(null);
      setEditData({});
    },
  });

  // Delete mutation
  const deleteMut = useMutation({
    mutationFn: (id) => referralAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['referral-links'] });
    },
  });

  // Toggle active
  const toggleActiveMut = useMutation({
    mutationFn: ({ id, is_active }) => referralAPI.update(id, { is_active }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['referral-links'] });
    },
  });

  const links = data?.links || [];
  const pagination = data?.pagination;

  const getReferralUrl = (code) => `${BASE_URL}/r/${code}`;

  const copyToClipboard = async (code, id) => {
    try {
      await navigator.clipboard.writeText(getReferralUrl(code));
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = getReferralUrl(code);
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  const handleCreate = (e) => {
    e.preventDefault();
    createMut.mutate({
      name: createForm.name,
      custom_code: createForm.custom_code || undefined,
      destination_url: createForm.destination_url || undefined,
      campaign_id: createForm.campaign_id || undefined,
    });
  };

  const startEditing = (link) => {
    setEditingId(link.id);
    setEditData({
      name: link.name,
      destination_url: link.destination_url || '',
      campaign_id: link.campaign_id || '',
    });
  };

  const handleUpdate = (id) => {
    updateMut.mutate({ id, data: editData });
  };

  const formatNumber = (n) => {
    if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
    return (n || 0).toString();
  };

  const stats = linkDetails?.stats;

  return (
    <div className="referral-links-page">
      <div className="referral-header">
        <div>
          <h1><Link2 size={28} /> Referral Links</h1>
          <p>Create custom trackable links to share in your promotions and earn credit for your sales.</p>
        </div>
        <button className="btn-create" onClick={() => setShowCreate(!showCreate)}>
          {showCreate ? <><X size={18} /> Cancel</> : <><Plus size={18} /> Create Link</>}
        </button>
      </div>

      {/* Summary stats */}
      {links.length > 0 && (
        <div className="referral-summary">
          <div className="summary-card">
            <div className="summary-label">Total Links</div>
            <div className="summary-value">{pagination?.total || links.length}</div>
          </div>
          <div className="summary-card">
            <div className="summary-label">Total Clicks</div>
            <div className="summary-value">{formatNumber(links.reduce((s, l) => s + (l.total_clicks || 0), 0))}</div>
          </div>
          <div className="summary-card">
            <div className="summary-label">Unique Clicks</div>
            <div className="summary-value">{formatNumber(links.reduce((s, l) => s + (l.unique_clicks || 0), 0))}</div>
          </div>
          <div className="summary-card">
            <div className="summary-label">Active Links</div>
            <div className="summary-value">{links.filter(l => l.is_active).length}</div>
          </div>
        </div>
      )}

      {/* Create form */}
      {showCreate && (
        <div className="create-form-card">
          <h3>Create a New Referral Link</h3>
          <form onSubmit={handleCreate}>
            <div className="form-grid">
              <div className="form-group">
                <label>Link Name *</label>
                <input
                  type="text"
                  placeholder="e.g., My Instagram Bio Link"
                  value={createForm.name}
                  onChange={(e) => setCreateForm(p => ({ ...p, name: e.target.value }))}
                  required
                />
              </div>

              <div className="form-group">
                <label>Custom Code (optional)</label>
                <div className="code-input-wrapper">
                  <span className="code-prefix">{BASE_URL}/r/</span>
                  <input
                    type="text"
                    placeholder="auto-generated"
                    value={createForm.custom_code}
                    onChange={(e) => setCreateForm(p => ({ ...p, custom_code: e.target.value.replace(/[^A-Za-z0-9_-]/g, '') }))}
                    maxLength={32}
                  />
                </div>
                <small>3-32 characters. Letters, numbers, dashes, underscores.</small>
              </div>

              <div className="form-group">
                <label>Destination URL (optional — can set later)</label>
                <input
                  type="url"
                  placeholder="https://example.com/shop"
                  value={createForm.destination_url}
                  onChange={(e) => setCreateForm(p => ({ ...p, destination_url: e.target.value }))}
                />
                <small>The offsite page visitors will be redirected to.</small>
              </div>

              <div className="form-group">
                <label>Campaign (optional)</label>
                <select
                  value={createForm.campaign_id}
                  onChange={(e) => setCreateForm(p => ({ ...p, campaign_id: e.target.value }))}
                >
                  <option value="">— No campaign —</option>
                  {campaigns.map(c => (
                    <option key={c.id} value={c.id}>{c.title}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-primary" disabled={createMut.isLoading || !createForm.name.trim()}>
                {createMut.isLoading ? 'Creating...' : 'Create Referral Link'}
              </button>
              {createMut.isError && (
                <span className="form-error">{createMut.error?.response?.data?.error || 'Failed to create link'}</span>
              )}
            </div>
          </form>
        </div>
      )}

      {/* Links list */}
      <div className="links-list">
        {isLoading ? (
          <SkeletonTable rows={5} columns={5} />
        ) : links.length === 0 ? (
          <div className="empty-state">
            <Link2 size={48} color="#9ca3af" />
            <h3>No Referral Links Yet</h3>
            <p>Create your first referral link to start tracking your promotions.</p>
            <button className="btn-primary" onClick={() => setShowCreate(true)}>
              <Plus size={18} /> Create Your First Link
            </button>
          </div>
        ) : (
          <>
            {links.map(link => (
              <div key={link.id} className={`link-card ${!link.is_active ? 'link-inactive' : ''}`}>
                <div className="link-card-main">
                  <div className="link-info">
                    <div className="link-name-row">
                      {editingId === link.id ? (
                        <input
                          type="text"
                          className="edit-name-input"
                          value={editData.name}
                          onChange={(e) => setEditData(p => ({ ...p, name: e.target.value }))}
                        />
                      ) : (
                        <h3 className="link-name">{link.name}</h3>
                      )}
                      {!link.is_active && <span className="badge-inactive">Inactive</span>}
                      {link.campaign && (
                        <span className="badge-campaign">{typeof link.campaign === 'object' ? link.campaign.title : 'Campaign'}</span>
                      )}
                    </div>

                    <div className="link-url-row">
                      <code className="link-url">{getReferralUrl(link.code)}</code>
                      <button
                        className={`btn-copy ${copiedId === link.id ? 'copied' : ''}`}
                        onClick={() => copyToClipboard(link.code, link.id)}
                        title="Copy link"
                      >
                        {copiedId === link.id ? <><Check size={14} /> Copied!</> : <><Copy size={14} /> Copy</>}
                      </button>
                    </div>

                    {link.destination_url ? (
                      <div className="link-destination">
                        {editingId === link.id ? (
                          <input
                            type="url"
                            className="edit-url-input"
                            value={editData.destination_url}
                            onChange={(e) => setEditData(p => ({ ...p, destination_url: e.target.value }))}
                            placeholder="https://example.com"
                          />
                        ) : (
                          <span className="dest-url">
                            <ExternalLink size={12} />
                            {link.destination_url.length > 60 ? link.destination_url.slice(0, 60) + '...' : link.destination_url}
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="link-destination no-dest">
                        {editingId === link.id ? (
                          <input
                            type="url"
                            className="edit-url-input"
                            value={editData.destination_url}
                            onChange={(e) => setEditData(p => ({ ...p, destination_url: e.target.value }))}
                            placeholder="https://example.com"
                          />
                        ) : (
                          <span>⚠️ No destination URL set — visitors will see a "Coming Soon" page</span>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="link-stats">
                    <div className="stat-item">
                      <span className="stat-number">{formatNumber(link.total_clicks)}</span>
                      <span className="stat-label">Clicks</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-number">{formatNumber(link.unique_clicks)}</span>
                      <span className="stat-label">Unique</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-number">{formatNumber(link.total_conversions)}</span>
                      <span className="stat-label">Conversions</span>
                    </div>
                  </div>

                  <div className="link-actions">
                    {editingId === link.id ? (
                      <>
                        <button className="btn-icon btn-save" onClick={() => handleUpdate(link.id)} title="Save">
                          <Check size={16} />
                        </button>
                        <button className="btn-icon btn-cancel" onClick={() => { setEditingId(null); setEditData({}); }} title="Cancel">
                          <X size={16} />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          className="btn-icon"
                          onClick={() => setExpandedLink(expandedLink === link.id ? null : link.id)}
                          title="View stats"
                        >
                          <BarChart3 size={16} />
                        </button>
                        <button className="btn-icon" onClick={() => startEditing(link)} title="Edit">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        </button>
                        <button
                          className={`btn-icon ${link.is_active ? 'btn-deactivate' : 'btn-activate'}`}
                          onClick={() => toggleActiveMut.mutate({ id: link.id, is_active: !link.is_active })}
                          title={link.is_active ? 'Deactivate' : 'Activate'}
                        >
                          {link.is_active ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                        <button
                          className="btn-icon btn-delete"
                          onClick={() => {
                            if (window.confirm('Delete this referral link? Click data will be lost.')) {
                              deleteMut.mutate(link.id);
                            }
                          }}
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Expanded stats panel */}
                {expandedLink === link.id && linkDetails && (
                  <div className="link-stats-panel">
                    <h4><BarChart3 size={16} /> Click Analytics (Last 30 Days)</h4>

                    {stats?.click_history?.length > 0 ? (
                      <div className="click-chart">
                        <div className="chart-bars">
                          {stats.click_history.map((day, i) => {
                            const maxClicks = Math.max(...stats.click_history.map(d => d.clicks));
                            const height = maxClicks > 0 ? (day.clicks / maxClicks) * 100 : 0;
                            return (
                              <div key={i} className="chart-bar-wrapper" title={`${day.date}: ${day.clicks} clicks (${day.unique_clicks} unique)`}>
                                <div className="chart-bar" style={{ height: `${Math.max(height, 2)}%` }}>
                                  <span className="chart-tooltip">{day.clicks}</span>
                                </div>
                                <span className="chart-label">{new Date(day.date).getDate()}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <p className="no-data">No clicks recorded yet.</p>
                    )}

                    <div className="stats-detail-grid">
                      <div className="stats-detail-card">
                        <h5><Globe size={14} /> Device Breakdown</h5>
                        {stats?.devices?.length > 0 ? (
                          <div className="device-list">
                            {stats.devices.map((d, i) => (
                              <div key={i} className="device-row">
                                <span className="device-icon">
                                  {d.device_type === 'mobile' ? <Smartphone size={14} /> :
                                   d.device_type === 'tablet' ? <Tablet size={14} /> :
                                   d.device_type === 'desktop' ? <Monitor size={14} /> :
                                   <Globe size={14} />}
                                </span>
                                <span className="device-name">{d.device_type || 'Unknown'}</span>
                                <span className="device-count">{d.count}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="no-data">No data yet</p>
                        )}
                      </div>

                      <div className="stats-detail-card">
                        <h5><MousePointerClick size={14} /> Top Referrers</h5>
                        {stats?.top_referrers?.length > 0 ? (
                          <div className="referrer-list">
                            {stats.top_referrers.map((r, i) => (
                              <div key={i} className="referrer-row">
                                <span className="referrer-url" title={r.referrer_url}>
                                  {(() => {
                                    try { return new URL(r.referrer_url).hostname; } catch { return r.referrer_url.slice(0, 30); }
                                  })()}
                                </span>
                                <span className="referrer-count">{r.count}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="no-data">No referrer data yet</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {pagination && pagination.total_pages > 1 && (
              <Pagination
                currentPage={pagination.page}
                totalPages={pagination.total_pages}
                total={pagination.total}
                limit={pagination.limit}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default ReferralLinks;
